import { useState, useRef } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

interface FeedPromptInputProps {
  userInitials: string;
  onBuildPrompt: (initialGoal: string) => void;
  filterControls: React.ReactNode;
}

const FeedPromptInput = ({ userInitials, onBuildPrompt, filterControls }: FeedPromptInputProps) => {
  const [inputValue, setInputValue] = useState("");
  const editableRef = useRef<HTMLDivElement>(null);

  const handleBuild = () => {
    if (!inputValue.trim()) return;
    onBuildPrompt(inputValue);
    setInputValue("");
    if (editableRef.current) {
      editableRef.current.textContent = "";
    }
  };

  const hasText = inputValue.trim().length > 0;

  return (
    <div className="flex flex-col gap-3">
      {/* Row 1: Avatar + Full-width text */}
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10 flex-shrink-0">
          <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">{userInitials}</AvatarFallback>
        </Avatar>
        <div
          ref={editableRef}
          contentEditable
          suppressContentEditableWarning
          className="flex-1 text-lg outline-none min-h-[28px] leading-relaxed empty:before:content-['What_are_you_trying_to_do?'] empty:before:text-muted-foreground empty:before:pointer-events-none"
          onInput={(e) => {
            const text = e.currentTarget.textContent || "";
            setInputValue(text);
            // Clear innerHTML when empty to ensure :empty pseudo-class works
            if (!text.trim() && e.currentTarget.innerHTML !== "") {
              e.currentTarget.innerHTML = "";
            }
          }}
        />
      </div>

      {/* Row 2: Button (left) + Icons (right) */}
      <div className="flex items-center justify-between pl-[52px]">
        <Button
          onClick={handleBuild}
          size="sm"
          variant="accent"
          className={`gap-2 transition-all duration-300 ${!hasText ? "opacity-75" : "opacity-100"}`}
        >
          Build a Prompt
          <ArrowRight className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-2">{filterControls}</div>
      </div>
    </div>
  );
};

export default FeedPromptInput;
